/**
 * admin/src/hooks/useSubscriptions.ts
 * Hooks cho Subscriptions management
 *
 * useSubscriptions   — danh sách có server-side filter
 * useSubsMutations   — tạo thủ công + xóa
 */

import { useState, useEffect, useCallback } from 'react';
import { adminFetch } from './useAdmin';
import { toast } from '../components/ui/index';
import type { AdminSubscription, PaginatedResponse } from '../types/admin';

// ─── Params ───────────────────────────────────────────────────────────────────
export interface SubsParams {
  page?: number;
  limit?: number;
  search?: string;
  plan?: 'basic' | 'premium' | 'family' | '';
  active?: boolean; // chỉ lấy active subs
}

interface UseSubscriptionsReturn {
  subs: AdminSubscription[];
  total: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useSubscriptions(params: SubsParams = {}): UseSubscriptionsReturn {
  const [data, setData] = useState<PaginatedResponse<AdminSubscription> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    adminFetch<PaginatedResponse<AdminSubscription>>('/subscriptions', {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
        search: params.search || undefined,
        plan: params.plan || undefined,
        active: params.active ? '1' : undefined,
      },
    })
      .then((res) => { if (!cancelled) { setData(res); setIsLoading(false); } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lỗi tải subscriptions');
          setIsLoading(false);
        }
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), tick]);

  return {
    subs: data?.data ?? [],
    total: data?.total ?? 0,
    totalPages: data?.total_pages ?? 1,
    isLoading,
    error,
    refetch,
  };
}

// ─── Mutations ────────────────────────────────────────────────────────────────
interface CreateSubPayload {
  user_id: string;
  plan: 'basic' | 'premium' | 'family';
  days: number;
  note?: string;
}

export function useSubsMutations(onSuccess?: () => void) {
  const [isSaving, setIsSaving] = useState(false);

  async function grantSubscription(payload: CreateSubPayload): Promise<boolean> {
    setIsSaving(true);
    try {
      const res = await adminFetch<{ ok: boolean; email: string; plan: string; days: number }>(
        '/subscriptions',
        { method: 'POST', body: JSON.stringify(payload) }
      );
      toast(
        `Đã cấp ${res.plan} (${payload.days} ngày) cho ${res.email}`,
        'success'
      );
      onSuccess?.();
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Lỗi cấp subscription', 'error');
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function cancelSubscription(subId: string, email: string): Promise<boolean> {
    setIsSaving(true);
    try {
      const res = await adminFetch<{ ok: boolean; newTier: string }>(
        `/subscriptions/${subId}`,
        { method: 'DELETE' }
      );
      toast(`Đã hủy sub của ${email} → tier: ${res.newTier}`, 'success');
      onSuccess?.();
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Lỗi hủy subscription', 'error');
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  return { grantSubscription, cancelSubscription, isSaving };
}
